import { Link } from 'react-router-dom';

import { ZERO } from '@/constants';
import { Typography } from '@/ui';

import type {
  ViewerSessionTime,
  ViewerSessionZone,
} from '@/features/membersTable/viewerSessionTime';
import type { FC, ReactNode } from 'react';

import ViewerSessionZoneLabel from '@/features/membersTable/ViewerSessionZoneLabel';
import { formatViewerSessionTime } from '@/features/membersTable/viewerSessionTime';

interface SessionColumnProps {
  done?: number;
  last?: string | null;
  next?: string | null;
  pastSessionsUrl: string;
  pending?: number;
  viewerZone: ViewerSessionZone;
  upcomingSessionsUrl: string;
  linkToMemberPages?: boolean;
}

const displayTime = (time: ViewerSessionTime, empty: string): ReactNode =>
  time.status === 'ready' ? time.dateTime : time.text || empty;

const zoneLabel = (time: ViewerSessionTime) =>
  time.status === 'ready' ? (
    <ViewerSessionZoneLabel
      shortZone={time.shortZone}
      timeZone={time.timeZone}
    />
  ) : null;

const SessionColumn: FC<SessionColumnProps> = ({
  done,
  last,
  next,
  pending,
  viewerZone,
  upcomingSessionsUrl,
  pastSessionsUrl,
  linkToMemberPages = true,
}) => {
  const upcoming =
    next === null || next === undefined
      ? null
      : formatViewerSessionTime(next, viewerZone, 'short');
  const completed =
    last === null || last === undefined
      ? null
      : formatViewerSessionTime(last, viewerZone, 'short');
  const upcomingText = upcoming
    ? displayTime(upcoming, '')
    : 'No upcoming sessions';
  const completedText = completed
    ? displayTime(completed, '')
    : 'No completed sessions yet';

  const upcomingCount = (
    <Typography
      className="text-nowrap"
      color="text.secondary"
      variant="caption"
    >
      {pending ?? ZERO} upcoming
    </Typography>
  );

  const completedCount = (
    <Typography
      className="text-nowrap"
      color="text.secondary"
      variant="caption"
    >
      {done ?? ZERO} completed
    </Typography>
  );

  return (
    <div className="flex flex-col gap-1">
      <Typography
        color={!pending ? 'error.main' : 'text.primary'}
        variant="subtitle2"
      >
        {linkToMemberPages ? (
          <Link
            style={{ color: 'inherit', textDecoration: 'none' }}
            to={upcomingSessionsUrl}
          >
            {upcomingText}
          </Link>
        ) : (
          upcomingText
        )}
        {upcoming && <> {zoneLabel(upcoming)}</>}
      </Typography>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        {linkToMemberPages ? (
          <Link
            color="text.secondary"
            style={{ display: 'flex', textDecoration: 'none' }}
            to={upcomingSessionsUrl}
          >
            {upcomingCount}
          </Link>
        ) : (
          upcomingCount
        )}
        {linkToMemberPages ? (
          <Link
            color="text.secondary"
            style={{ display: 'flex', textDecoration: 'none' }}
            to={pastSessionsUrl}
          >
            {completedCount}
          </Link>
        ) : (
          completedCount
        )}
      </div>

      <Typography color="text.secondary" variant="caption">
        {linkToMemberPages ? (
          <Link
            color="text.secondary"
            style={{ textDecoration: 'none' }}
            to={pastSessionsUrl}
          >
            Last: {completedText}
          </Link>
        ) : (
          <>Last: {completedText}</>
        )}
        {completed && <> {zoneLabel(completed)}</>}
      </Typography>
    </div>
  );
};

export default SessionColumn;
